import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import {
  Card,
  CardContent,
  CardHeader,
  TextField,
  MenuItem,
  Button as MuiButton,
  Select as MuiSelect,
  FormControl,
  InputLabel,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  IconButton,
  Switch,
  FormControlLabel,
  Tooltip,
  Checkbox,
  Stack,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios, { AxiosError } from "axios";
import { ja } from "date-fns/locale";
import { format } from "date-fns-tz";
import { enqueueSnackbar } from "notistack";
import { HTMLAttributes, Key, useState, useEffect, useContext, useCallback } from "react";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import LicenseContext from "src/contexts/LicenseContext";
import {
  ItemKlassOptions,
  ItemKlass,
  ItemHandlingOfCargoStyleOptions,
  ItemHandlingOfCargoStyle,
  DesignatedTruckCarModelOptions,
  DesignatedTruckKlassOptions,
  DesignatedTruckLoadingPlatformHeightOptions,
  DesignatedTruckLoadingPlatformWidthOptions,
  DesignatedTruckLoadingPlatformLengthOptions,
  DesignatedTruckFloorSpecificationOptions,
  DesignatedTruckFeatureOptions,
  DesignatedTruckKlass,
  DesignatedTruckCarModel,
  DesignatedTruckLoadingPlatformHeight,
  DesignatedTruckLoadingPlatformLength,
  DesignatedTruckLoadingPlatformWidth,
  DesignatedTruckFloorSpecification,
  DesignatedTruckFeature,
  ItemPackageStyle,
  ItemPackageStyleOptions,
  TruckDriverIdAndName
} from "src/entities/orderEntity";
import { PlaceEntity } from "src/entities/placeEntity";
import { ProductEntity } from "src/entities/ProductEntity";
import { ShipperEntity } from "src/entities/shipperEntity";
import { useDriversQuery } from 'src/hooks/useDrivers.query';
import { useAllProductsRequest } from "src/hooks/useProducts.request";
import { useQueryCompany } from "src/hooks/useQueryCompany";
import { useQueryPlaces } from "src/hooks/useQueryPlaces";
import { useMutationShipmentOrder, useQueryShipmentOrder } from "src/hooks/useQueryShipmentOrders";
import { useQueryShippers } from "src/hooks/useQueryShippers";
import { useTrucksQuery } from 'src/hooks/useTrucks.query';
import { computeAutoFillItemWeightVolume } from "src/utils/autoFillItemWeightVolume";
import {
  isValidLoadingUnloadingDateRange,
  LOADING_UNLOADING_DATE_RANGE_ERROR_MESSAGE,
} from "src/utils/shipmentOrder.util";

import { ConfirmDialog } from "../ConfirmDialog";
import { ShipperOption } from "../selectors/ShipperOption";
import { TruckDriverConstraintsSelector } from "../ui/shipmentOrders/TruckDriverConstraintsSelector";

export interface ShipmentOrderFormData {
  id?: number;
  companyId?: number;
  code?: string;
  memo?: string;
  deliveryMethod?: "貸切" | "共配" | "路線便" | '';
  customInputFields: CustomInputField[];
  allowedTrucks?: TruckDriverIdAndName[];
  deniedDrivers?: TruckDriverIdAndName[];
  allowedTruckIds?: number[];
  deniedDriverIds?: number[];
  shipperName: string;
  shipperContactPerson: string;
  shipperEmailAddress: string;
  shipperPhoneNumber: string;
  itemKlass: ItemKlass | '';
  itemHandlingOfCargoStyle: ItemHandlingOfCargoStyle | '';
  itemCanBeMixed: boolean;
  designatedTruckCarModels: DesignatedTruckCarModel[]; // 指定車種
  designatedTruckFeatures: DesignatedTruckFeature[]; // 設備
  designatedTruckFloorSpecifications: DesignatedTruckFloorSpecification[]; // 床仕様
  designatedTruckLoadingPlatformHeights: DesignatedTruckLoadingPlatformHeight[]; // 荷台高さ
  designatedTruckLoadingPlatformLengths: DesignatedTruckLoadingPlatformLength[]; // 荷台長さ
  designatedTruckLoadingPlatformWidths: DesignatedTruckLoadingPlatformWidth[]; // 荷台幅
  designatedTruckKlasses: DesignatedTruckKlass[]; // 指定車両タイプ
  pickupId?: number;
  pickupLocation: string;
  pickupAddress: string;
  pickupLatitude?: string;
  pickupLongitude?: string;
  skipPickupGeocoding?: boolean;
  pickupDate: Date;
  pickupTimeSlot: string;
  pickupCustomTimes?: { id?: number, date?: Date, startTime: string; endTime: string }[];
  pickupDuration: number | '';
  deliveryId?: number;
  deliveryLocation: string;
  deliveryAddress: string;
  deliveryLatitude?: string;
  deliveryLongitude?: string;
  skipDeliveryGeocoding?: boolean;
  deliveryDate: Date;
  deliveryTimeSlot: string;
  deliveryCustomTimes?: { id?: number, date?: Date, startTime: string; endTime: string }[];
  deliveryDuration: number | '';
  shipmentOrderItems: ShipmentOrderItemData[];
  intermediateLocations: IntermediateLocationData[];
  billing: {
    id?: number;
    basicFeeYen: number | '';
    surchargeFeeYen: number | '';
    relayFeeYen: number | '';
    highwayFeeYen: number | '';
    loadingFeeYen: number | '';
    ancillaryFeeYen: number | '';
    waitingTimeFeeYen: number | '';
    unloadingFeeYen: number | '';
    expensesFeeYen: number | '';
    ancillaryContent: string;
  };
  waypoints: Waypoint[];
}

interface CustomInputField {
  key: string;
  value: string;
}

interface IntermediateLocationData {
  name: string;
  address: string;
  latitude?: string;
  longitude?: string;
  skipGeocoding?: boolean;
  dayPickup: boolean;
  eveningPickup: boolean;
}

interface ShipmentOrderItemData {
  id?: number;
  shipmentOrderId?: number;
  productCode: string | null;
  productName: string | null;
  quantity: number | '';
  itemPackingStyle: ItemPackageStyle | null;
  weight_kg: number | '';
  volume_m3: number | '';
  lotNumber: string;
  customOrderNumber: string | null;
  entity?: ProductEntity | null;
  weightKgManuallyEdited: boolean;
  volumeM3ManuallyEdited: boolean;
}

// API リクエスト用の sanitized 型。画面 state 専用フィールド (entity / *ManuallyEdited)
// を除外し、submit 時に意図せず混入することを型で防ぐ。`Omit` を使うことで、
// 将来 `ShipmentOrderItemData` に API 送信が必要なフィールドが追加されたら
// 自動的に追従する。
// TODO: `ShipmentOrderFormData` 自体が画面 state と API payload の両方を表しており、
// `useMutationShipmentOrder` の引数型も含めて将来分離する。
type ShipmentOrderItemPayload = Omit<
  ShipmentOrderItemData,
  'entity' | 'weightKgManuallyEdited' | 'volumeM3ManuallyEdited'
>;

interface ShipmentOrderFormProps {
  baseDate?: Date;
  startOn: string;
  newShipmentDialogOpen: boolean;
  setNewShipmentDialogOpen: (visible: boolean) => void;
  shipmentOrderId?: number;
}

type TimeWindow = { id?: number; startAt: string; endAt: string };

interface Waypoint {
  id?: number;
  sequence_number: number;
  kind: string;
  name: string;
  address: string;
  latitude?: string;
  longitude?: string;
  skipGeocoding?: boolean;
  stayingMinutes: number | '';
  timeWindows: TimeWindow[];
}

export const ShipmentOrderForm: React.FC<ShipmentOrderFormProps> = ({
  baseDate,
  startOn,
  newShipmentDialogOpen,
  setNewShipmentDialogOpen,
  shipmentOrderId,
}) => {
  const queryClient = useQueryClient();
  const { data: shipmentOrderData, isLoading: shipmentOrderIsLoading } = useQueryShipmentOrder(shipmentOrderId);

  const licenseContext = useContext(LicenseContext);
  const [companyId, setCompanyId] = useState(0);

  const shippersQuery = useQueryShippers(true);
  const placesQuery = useQueryPlaces(true);
  // per は「同時に飛ぶリクエスト本数」を決める。useAllProductsRequest は page 1 を取得後、
  // 残りページを並列取得するため、per が小さいと商品件数に比例して同時接続が増える。
  // 荷主・地点が使う v2 API の DEFAULT_PER_PAGE(5000) に合わせて大きめに取る。
  const productsRequest = useAllProductsRequest('', '', newShipmentDialogOpen, 1000);

  const { data: truckData } = useTrucksQuery(startOn, { enabled: newShipmentDialogOpen });
  const { data: driverData } = useDriversQuery(startOn, { enabled: newShipmentDialogOpen });

  const [shippers, setShippers] = useState<ShipperEntity[]>([]);
  const [places, setPlaces] = useState<PlaceEntity[]>([]);
  const pickupPlaces = places.filter(p => !p.location_role || p.location_role !== 'unloading');
  const deliveryPlaces = places.filter(p => !p.location_role || p.location_role !== 'loading');
  const [products, setProducts] = useState<ProductEntity[]>([]);
  const { data: companyData } = useQueryCompany(companyId);

  const [confirmDiaalogOpen, setConfirmDialogOpen] = useState(false);
  const [modified, setModified] = useState(false);
  const [editable, setEditable] = useState(true);
  const [allowedTrucks, setAllowedTrucks] = useState<TruckDriverIdAndName[]>([]);
  const [deniedDrivers, setDeniedDrivers] = useState<TruckDriverIdAndName[]>([]);
  const [shipperInfoExpanded, setShipperInfoExpanded] = useState(false);
  const [totalAmount, setTotalAmount] = useState(0);
  const [customInputFields, setCustomInputFields] = useState<string[]>([]);
  const [registrationUser, setRegistrationUser] = useState<string>('');
  // 登録ボタンとキャンセルボタンのローディング状態を管理
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [timeSettings, setTimeSettings] = useState<{ kind: string, startAt: string, endAt: string }[]>([
    { kind: 'AM', startAt: '08:00', endAt: '12:00' },
    { kind: 'PM', startAt: '13:00', endAt: '18:00' },
    { kind: 'ALL', startAt: '08:00', endAt: '20:00' },
  ]);

  const { createShipmentOrder } = useMutationShipmentOrder();
  const deleteShipper = useMutation({
    mutationFn: (shipperId: number) => axios.delete(`/api/v2/shippers/${shipperId}`),

    onSuccess: async (_, shipperId) => {
      const deletedShipper = shippers.find((shipper) => shipper.id === shipperId);
      if (deletedShipper && getValues("shipperName") === deletedShipper.name) {
        setValue("shipperName", "", { shouldDirty: true });
        setValue("shipperContactPerson", "", { shouldDirty: true });
        setValue("shipperPhoneNumber", "", { shouldDirty: true });
        setValue("shipperEmailAddress", "", { shouldDirty: true });
      }
      setShippers((current) => current.filter((shipper) => shipper.id !== shipperId));
      await queryClient.invalidateQueries({
        queryKey: ['shippers']
      });
    }
  });

  useEffect(() => {
    if (!licenseContext.config) return;

    const config = licenseContext.config;
    setCompanyId(config.selected_company_id || config.company_id)
    setRegistrationUser(config.person_name || config.email);
  }, [licenseContext.config]);

  useEffect(() => {
    const dt = baseDate || new Date();
    setValue("pickupDate", dt);
    setValue("deliveryDate", dt);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseDate]);

  useEffect(() => {
    if (!shippersQuery.data) return;

    // 名前の重複を除去（最初に見つかったもので重複を除外）
    const uniqueShippers = shippersQuery.data.filter((shipper, index, array) =>
      array.findIndex(s => s.name === shipper.name) === index
    );

    setShippers(uniqueShippers);
  }, [shippersQuery.data]);

  useEffect(() => {
    if (!placesQuery.data) return;

    // 名前の重複を除去（最初に見つかったもので重複を除外）
    const uniquePlaces = placesQuery.data.filter((place, index, array) =>
      array.findIndex(p => p.name === place.name) === index
    );
    setPlaces(uniquePlaces);
  }, [placesQuery.data]);

  useEffect(() => {
    if (!productsRequest.data) return;

    setProducts(productsRequest.data);
  }, [productsRequest.data]);

  const { register, handleSubmit, setValue, getValues, watch, reset, formState, control } = useForm<ShipmentOrderFormData>({
    defaultValues: {
      pickupDate: baseDate || new Date(),
      deliveryDate: baseDate || new Date(),
      skipPickupGeocoding: false,
      skipDeliveryGeocoding: false,
      pickupTimeSlot: "AM",
      deliveryTimeSlot: "AM",
      deliveryMethod: "",
      pickupCustomTimes: [{ date: baseDate || new Date(), startTime: "", endTime: "" }],
      deliveryCustomTimes: [{ date: baseDate || new Date(), startTime: "", endTime: "" }],
      intermediateLocations: [],
      itemKlass: "",
      itemHandlingOfCargoStyle: "",
      itemCanBeMixed: true,
      designatedTruckKlasses: [],
      designatedTruckCarModels: [],
      designatedTruckLoadingPlatformHeights: [],
      designatedTruckLoadingPlatformWidths: [],
      designatedTruckLoadingPlatformLengths: [],
      designatedTruckFloorSpecifications: [],
      designatedTruckFeatures: [],
      billing: {
        basicFeeYen: null,
        surchargeFeeYen: null,
        relayFeeYen: null,
        highwayFeeYen: null,
        loadingFeeYen: null,
        ancillaryFeeYen: null,
        waitingTimeFeeYen: null,
        unloadingFeeYen: null,
        expensesFeeYen: null,
        ancillaryContent: ""
      },
      shipmentOrderItems: [{ id: undefined, productCode: null, productName: null, itemPackingStyle: null, quantity: null, weight_kg: null, volume_m3: null, lotNumber: null, customOrderNumber: null, entity: null, weightKgManuallyEdited: false, volumeM3ManuallyEdited: false }],
      customInputFields: customInputFields.map(field => ({ key: field, value: "" })),
      allowedTrucks: [],
      deniedDrivers: [],
      allowedTruckIds: [],
      deniedDriverIds: [],
    }
  });

  const {
    fields: intermediateLocationFields,
    append: appendIntermediateLocation,
    remove: removeIntermediateLocationField,
  } = useFieldArray({
    control,
    name: "intermediateLocations"
  });

  const {
    fields: pickupCustomTimeFields,
    append: appendPickupCustomTime,
    remove: removePickupCustomTimeField,
  } = useFieldArray({
    control,
    name: "pickupCustomTimes"
  });

  const {
    fields: deliveryCustomTimeFields,
    append: appendDeliveryCustomTime,
    remove: removeDeliveryCustomTimeField,
  } = useFieldArray({
    control,
    name: "deliveryCustomTimes"
  });

  const {
    fields: itemFields,
    append: appendItem,
    remove: removeItemField,
  } = useFieldArray({
    control,
    name: "shipmentOrderItems"
  });

  useEffect(() => {
    setModified(formState.isDirty);
  }, [formState.isDirty]);

  useEffect(() => {
    if (!newShipmentDialogOpen) return;
    setModified(false);
  }, [newShipmentDialogOpen, reset]);

  useEffect(() => {
    if (shipmentOrderIsLoading) return;
    if (!shipmentOrderData) return;

    const loadingWaypoint = shipmentOrderData.shipmentOrderLoadingWaypoint;
    const loadingTimes = loadingWaypoint.shipmentOrderWaypointLoadingTimes || [];
    const unloadingWaypoint = shipmentOrderData.shipmentOrderUnloadingWaypoint;
    const unloadingTimes = unloadingWaypoint.shipmentOrderWaypointUnloadingTimes || [];
    const transferWaypoints = shipmentOrderData.shipmentOrderTransferWaypoints || [];

    if (shipmentOrderData) {
      setEditable(shipmentOrderData.allocated === false && shipmentOrderData.wasSplit === false);
      reset({
        ...shipmentOrderData,
        pickupId: loadingWaypoint.id,
        pickupLocation: loadingWaypoint.name,
        pickupAddress: loadingWaypoint.address,
        pickupLatitude: loadingWaypoint.latitude,
        pickupLongitude: loadingWaypoint.longitude,
        skipPickupGeocoding: loadingWaypoint.skipGeocoding ?? false,
        pickupDate: new Date(loadingTimes[0]?.startAt),
        pickupTimeSlot: 'カスタム',
        pickupCustomTimes: loadingTimes.map((time) => ({ id: time.id, date: new Date(time.startAt), startTime: format(new Date(time.startAt), 'HH:mm'), endTime: format(new Date(time.endAt), 'HH:mm') })),
        pickupDuration: loadingWaypoint.stayingSeconds === '' ? '' : Math.floor(loadingWaypoint.stayingSeconds / 60),
        deliveryId: unloadingWaypoint.id,
        deliveryLocation: unloadingWaypoint.name,
        deliveryAddress: unloadingWaypoint.address,
        deliveryLatitude: unloadingWaypoint.latitude,
        deliveryLongitude: unloadingWaypoint.longitude,
        skipDeliveryGeocoding: unloadingWaypoint.skipGeocoding ?? false,
        deliveryDate: new Date(unloadingTimes[0]?.startAt),
        deliveryTimeSlot: 'カスタム',
        deliveryCustomTimes: unloadingTimes.map((time) => ({ id: time.id, date: new Date(time.startAt), startTime: format(new Date(time.startAt), 'HH:mm'), endTime: format(new Date(time.endAt), 'HH:mm') })),
        deliveryDuration: unloadingWaypoint.stayingSeconds === '' ? '' : Math.floor(unloadingWaypoint.stayingSeconds / 60),
        intermediateLocations: transferWaypoints.map((waypoint) => ({
          name: waypoint.name,
          address: waypoint.address,
          latitude: waypoint.latitude,
          longitude: waypoint.longitude,
          dayPickup: false
        })),
        billing: shipmentOrderData.shipmentOrderLegs?.[0]?.shipmentOrderLegRuns?.[0]?.billingAmount || {
          basicFeeYen: null,
          surchargeFeeYen: null,
          relayFeeYen: null,
          highwayFeeYen: null,
          loadingFeeYen: null,
          ancillaryFeeYen: null,
          waitingTimeFeeYen: null,
          unloadingFeeYen: null,
          expensesFeeYen: null,
          ancillaryContent: "",
        },
        shipmentOrderItems: shipmentOrderData.shipmentOrderItems.map((item) => ({
          id: item.id,
          shipmentOrderId: item.shipmentOrderId,
          productCode: item.productCode,
          productName: item.productName,
          quantity: item.quantity,
          itemPackingStyle: item.itemPackingStyle,
          weight_kg: item.weightKg,
          volume_m3: item.volumeM3,
          lotNumber: item.lotNumber,
          customOrderNumber: item.customOrderNumber,
          entity: null,
          weightKgManuallyEdited: item.weightKg !== '' && item.weightKg != null,
          volumeM3ManuallyEdited: item.volumeM3 !== '' && item.volumeM3 != null,
        })),
      });
      if (shipmentOrderData.shipmentOrderItems.length === 0) {
        addProduct();
      }
    }
  }, [shipmentOrderData, shipmentOrderIsLoading]);

  useEffect(() => {
    if (!companyData) return;
    setCustomInputFields(companyData.custom_input_fields);
  }, [companyData]);

  useEffect(() => {
    if (!companyData) return;
    const ranges = companyData.company_order_time_ranges_attributes || [];

    setTimeSettings((prev) => prev.map((it) => {
        const range = ranges.find((rng) => it.kind === rng.kind);
        if (range) {
          return {
            ...it,
            startAt: range.start_at,
            endAt: range.end_at,
          };
        }
        return it;
      }));
  }, [companyData]);

  // 請求合計金額のリアルタイム計算
  const billingData = watch("billing");
  useEffect(() => {
    const total = (billingData?.basicFeeYen || 0) +
                  (billingData?.surchargeFeeYen || 0) +
                  (billingData?.relayFeeYen || 0) +
                  (billingData?.highwayFeeYen || 0) +
                  (billingData?.loadingFeeYen || 0) +
                  (billingData?.ancillaryFeeYen || 0) +
                  (billingData?.waitingTimeFeeYen || 0) +
                  (billingData?.unloadingFeeYen || 0) +
                  (billingData?.expensesFeeYen || 0);
    setTotalAmount(total);
  }, [
    billingData?.basicFeeYen,
    billingData?.surchargeFeeYen,
    billingData?.relayFeeYen,
    billingData?.highwayFeeYen,
    billingData?.loadingFeeYen,
    billingData?.ancillaryFeeYen,
    billingData?.waitingTimeFeeYen,
    billingData?.unloadingFeeYen,
    billingData?.expensesFeeYen
  ]);

  // 区間種別の自動設定と管理
  const [sectionTypes, setSectionTypes] = useState<string[]>(["配送"]);

  useEffect(() => {
    console.log("中継地数が変更されました:", intermediateLocationFields.length);
    if (intermediateLocationFields.length === 0) {
      // 中継地がない場合：1区間（積地→降地）
      console.log("区間設定: 配送のみ");
      setSectionTypes(["配送"]);
    } else if (intermediateLocationFields.length === 1) {
      // 中継地が1つの場合：2区間（積地→中継地、中継地→降地）
      console.log("区間設定: 集荷→配送");
      setSectionTypes(["集荷", "配送"]);
    } else if (intermediateLocationFields.length === 2) {
      // 中継地が2つの場合：3区間（積地→中継地1、中継地1→中継地2、中継地2→降地）
      console.log("区間設定: 集荷→横持ち→配送");
      setSectionTypes(["集荷", "横持ち", "配送"]);
    }
  }, [intermediateLocationFields.length]);

  const updateSectionType = (index: number, value: string) => {
    const newSectionTypes = [...sectionTypes];
    newSectionTypes[index] = value;
    setSectionTypes(newSectionTypes);
  };

  const timeSlotOptions = ["AM", "PM", "ALL", "カスタム"];

  // トラックデータをTruckDriverIdAndName形式に変換
  const trucksOptions: TruckDriverIdAndName[] = truckData?.map(truck => ({
    id: truck.id,
    name: truck.licensePlateValue
  })) || [];

  // ドライバーデータをTruckDriverIdAndName形式に変換
  const driversOptions: TruckDriverIdAndName[] = driverData?.map(driver => ({
    id: driver.id,
    name: driver.name
  })) || [];

  // 指定トラック変更ハンドラー
  const handleChangeAllowedTrucks = useCallback((_, value: TruckDriverIdAndName[]) => {
    setValue("allowedTrucks", value);
    setValue("allowedTruckIds", value.map(t => t.id));
    setAllowedTrucks(value);
    setModified(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // NGドライバー変更ハンドラー
  const handleChangeDeniedDrivers = useCallback((_, value: TruckDriverIdAndName[]) => {
    setValue("deniedDrivers", value);
    setValue("deniedDriverIds", value.map(d => d.id));
    setDeniedDrivers(value);
    setModified(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addProduct = useCallback(() => {
    appendItem({ id: undefined, productCode: null, productName: null, itemPackingStyle: null, quantity: null, weight_kg: null, volume_m3: null, lotNumber: null, customOrderNumber: null, entity: null, weightKgManuallyEdited: false, volumeM3ManuallyEdited: false });
  }, [appendItem]);

  const removeProduct = (index: number) => {
    if (itemFields.length > 1) {
      removeItemField(index);
    }
  };

  const updateProduct = (index: number, field: keyof ShipmentOrderItemData, value: string | number) => {
    setValue(`shipmentOrderItems.${index}.${field}`, value, { shouldDirty: true });
  };

  const addIntermediateLocation = () => {
    if (intermediateLocationFields.length < 2) {
      appendIntermediateLocation({ name: "", address: "", dayPickup: true, eveningPickup: false });
    }
  };

  const removeIntermediateLocation = (index: number) => {
    removeIntermediateLocationField(index);
  };

  const updateIntermediateLocation = (index: number, field: keyof IntermediateLocationData, value: string | PlaceEntity | boolean) => {
    if (typeof value === 'object') {
      setValue(`intermediateLocations.${index}.name`, value.name, { shouldDirty: true });
      setValue(`intermediateLocations.${index}.address`, value.address || "", { shouldDirty: true });
      setValue(`intermediateLocations.${index}.latitude`, value.latitude || undefined, { shouldDirty: true });
      setValue(`intermediateLocations.${index}.longitude`, value.longitude || undefined, { shouldDirty: true });
      setValue(`intermediateLocations.${index}.skipGeocoding`, true, { shouldDirty: true });
    } else {
      setValue(`intermediateLocations.${index}.${field}`, value, { shouldDirty: true });
    }
  };

  const addCustomTime = (type: 'pickup' | 'delivery') => {
    if (type === 'pickup' && pickupCustomTimeFields.length < 3) {
      appendPickupCustomTime({ date: watch("pickupDate"), startTime: "", endTime: "" });
    } else if (type === 'delivery' && deliveryCustomTimeFields.length < 3) {
      appendDeliveryCustomTime({ date: watch("deliveryDate"), startTime: "", endTime: "" });
    }
  };

  const removeCustomTime = (type: 'pickup' | 'delivery', index: number) => {
    if (type === 'pickup' && pickupCustomTimeFields.length > 1) {
      removePickupCustomTimeField(index);
    } else if (type === 'delivery' && deliveryCustomTimeFields.length > 1) {
      removeDeliveryCustomTimeField(index);
    }
  };

  const updateCustomTime = (type: 'pickup' | 'delivery', index: number, field: 'startTime' | 'endTime', value: string) => {
    if (type === 'pickup') {
      setValue(`pickupCustomTimes.${index}.${field}`, value, { shouldDirty: true });
    } else {
      setValue(`deliveryCustomTimes.${index}.${field}`, value, { shouldDirty: true });
    }
  };

  const handleCancel = () => {
    if (modified) {
      setConfirmDialogOpen(true);
    } else {
      setNewShipmentDialogOpen(false);
    }
  };

  const handleConfirmOK = () => {
    setConfirmDialogOpen(false);
    setNewShipmentDialogOpen(false);
  };

  const handleConfirmCancel = () => {
    setConfirmDialogOpen(false);
  };

  // 時間文字列とDateオブジェクトから新しいDateオブジェクトを作成する共通メソッド
  const createDateTimeFromTimeString = (date: Date, timeString: string): string => {
    const dateTime = new Date(date);
    const [hours, minutes] = timeString.split(':');
    dateTime.setHours(Number(hours));
    dateTime.setMinutes(Number(minutes));
    return format(dateTime, "yyyy-MM-dd'T'HH:mm", { timeZone: 'Asia/Tokyo' });
  };

  const buildTimeWindows = (
    timeSlot: string,
    date: Date,
    customTimes?: { id?: number; date?: Date; startTime: string; endTime: string }[]
  ): TimeWindow[] => {
    const timeSetting = timeSettings.find((it) => it.kind === timeSlot);
    if (timeSetting) {
      const startDt = createDateTimeFromTimeString(date, timeSetting.startAt);
      const endDt = createDateTimeFromTimeString(date, timeSetting.endAt);
      return [{ startAt: startDt, endAt: endDt }];
    }
    if (timeSlot === "カスタム" && customTimes) {
      return customTimes
        .filter((time) => time.startTime && time.endTime)
        .map((time) => {
          // カスタム時間に日付が指定されている場合はそれを使用、なければデフォルトの日付を使用
          const targetDate = time.date || date;
          const startDt = createDateTimeFromTimeString(targetDate, time.startTime);

          // 終了時刻が開始時刻よりも早い場合は翌日として扱う
          let endDate = targetDate;
          if (time.endTime < time.startTime) {
            endDate = new Date(targetDate);
            endDate.setDate(endDate.getDate() + 1);
          }
          const endDt = createDateTimeFromTimeString(endDate, time.endTime);

          return { id: time.id, startAt: startDt, endAt: endDt };
        });
    }
    return [];  // デフォルトは空配列
  };

  const buildWaypoints = (data: ShipmentOrderFormData) => {
    const waypoints: Waypoint[] = [];
    let sequenceNumber = 0;
    // 積地
    waypoints.push({
      id: shipmentOrderData?.shipmentOrderLoadingWaypoint?.id || null,
      sequence_number: sequenceNumber++,
      kind: "loading",
      name: data.pickupLocation,
      address: data.pickupAddress,
      latitude: data.pickupLatitude,
      longitude: data.pickupLongitude,
      skipGeocoding: data.skipPickupGeocoding,
      stayingMinutes: data.pickupDuration,
      timeWindows: buildTimeWindows(data.pickupTimeSlot, data.pickupDate, data.pickupCustomTimes),
    });
    // 中継地
    data.intermediateLocations.forEach((loc, index) => {
      waypoints.push({
        id: shipmentOrderData?.shipmentOrderTransferWaypoints[index]?.id || null,
        sequence_number: sequenceNumber++,
        kind: "transfer",
        name: loc.name,
        address: loc.address,
        latitude: loc.latitude,
        longitude: loc.longitude,
        skipGeocoding: loc.skipGeocoding,
        stayingMinutes: (loc.dayPickup ? 30 : 0) + (loc.eveningPickup ? 30 : 0),
        timeWindows: [],  // 中継地の時間指定は未対応
      });
    });
    // 降地
    waypoints.push({
      id: shipmentOrderData?.shipmentOrderUnloadingWaypoint?.id || null,
      sequence_number: sequenceNumber++,
      kind: "unloading",
      name: data.deliveryLocation,
      address: data.deliveryAddress,
      latitude: data.deliveryLatitude,
      longitude: data.deliveryLongitude,
      skipGeocoding: data.skipDeliveryGeocoding,
      stayingMinutes: data.deliveryDuration,
      timeWindows: buildTimeWindows(data.deliveryTimeSlot, data.deliveryDate, data.deliveryCustomTimes),
    });

    return waypoints;
  };

  const validateLoadingUnloadingDateRange = (data: ShipmentOrderFormData): boolean => {
    // 積日・降日 180日制限バリデーション（buildTimeWindows と同様に endTime < startTime の翌日終了を考慮）
    const pickupTimeWindows = buildTimeWindows(data.pickupTimeSlot, data.pickupDate, data.pickupCustomTimes);
    const deliveryTimeWindows = buildTimeWindows(data.deliveryTimeSlot, data.deliveryDate, data.deliveryCustomTimes);
    const loadingDate = pickupTimeWindows.length
      ? new Date(Math.min(...pickupTimeWindows.map((tw) => new Date(tw.startAt).getTime())))
      : data.pickupDate;
    const unloadingDate = deliveryTimeWindows.length
      ? new Date(Math.max(...deliveryTimeWindows.map((tw) => new Date(tw.endAt).getTime())))
      : data.deliveryDate;
    return isValidLoadingUnloadingDateRange(loadingDate, unloadingDate);
  };

  const handleFormSubmit = (data: ShipmentOrderFormData) => {
    console.log("登録ボタンがクリックされました", data);

    if (!validateLoadingUnloadingDateRange(data)) {
      enqueueSnackbar(LOADING_UNLOADING_DATE_RANGE_ERROR_MESSAGE, { variant: "error" });
      return;
    }

    // ローディング状態を開始
    setIsSubmitting(true);

    // internal フィールド (entity / *ManuallyEdited) は API に送らない。
    // rest 構文で除外することで `ShipmentOrderItemPayload` (Omit ベース) と実装が同期し、
    // 将来 `ShipmentOrderItemData` にフィールドが追加されても自動追従する。
    const sanitizedItems: ShipmentOrderItemPayload[] = data.shipmentOrderItems.map(
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      ({ entity, weightKgManuallyEdited, volumeM3ManuallyEdited, ...rest }) => rest,
    );

    // フォームデータに区間種別を追加
    const submitData = {
      ...data,
      shipmentOrderItems: sanitizedItems,
      sectionTypes: sectionTypes,
      intermediateLocations: intermediateLocationFields,
      totalAmount: totalAmount,
      submittedAt: new Date().toISOString(),
      waypoints: buildWaypoints(data),
      customInputFields: data.customInputFields.filter((it) => it.value?.length > 0),
    };

    console.log("伝票登録データ:", submitData);

    // const action = shipmentOrderId ? updateShipmentOrder : createShipmentOrder;
    // TODO: リクエストデータが画面の形式のままなのが不適切。必要最小限に修正が必要。
    // `submitData.shipmentOrderItems` は sanitize 済み (`ShipmentOrderItemPayload[]`) で
    // ShipmentOrderFormData が要求する `weightKgManuallyEdited` / `volumeM3ManuallyEdited`
    // を持たないため、型を一旦 unknown 経由で合わせている。`mutateAsync` の引数型を
    // payload 型に変更すれば撤廃可能 (上の TODO と統合)。
    createShipmentOrder.mutateAsync(submitData as unknown as ShipmentOrderFormData).then(() => {
      enqueueSnackbar('伝票を登録しました', { variant: 'success' });
    }).then(() => {
      setNewShipmentDialogOpen(false);
    }).catch((error: AxiosError<{ errors: string[] }>) => {
      console.log("伝票登録エラー詳細:", error);
      if (error.response?.data && error.response.data.errors) {
        enqueueSnackbar(`伝票登録エラー: ${error.response.data.errors.join(', ')}`, { variant: 'error' });
      } else {
        enqueueSnackbar(`伝票登録エラー: ${error.message}`, { variant: 'error' });
      }
    }).finally(() => {
      // 処理が完了したらローディング状態を解除
      setIsSubmitting(false);
    });
  };

  const handleShipperChange = (_event, value: string | ShipperEntity | null) => {
    if (!value) {
      setValue("shipperName", "", { shouldDirty: true });
      return;
    }

    let selectedShipper: ShipperEntity | undefined;

    if (typeof value === "string") {
      // 自由入力の場合
      setValue("shipperName", value, { shouldDirty: true });

      // 同じ名前の荷主がshippersに存在するかチェック
      selectedShipper = shippers.find((shipper) => shipper.name === value);
    } else {
      selectedShipper = value;
    }
    if (selectedShipper) {
      setValue("shipperName", selectedShipper.name, { shouldDirty: true });
      setValue("shipperContactPerson", selectedShipper.contact_person || '', { shouldDirty: true });
      setValue("shipperPhoneNumber", selectedShipper.phone_number || '', { shouldDirty: true });
      setValue("shipperEmailAddress", selectedShipper.email_address || '', { shouldDirty: true });
      setShipperInfoExpanded(
        !!(selectedShipper.name || selectedShipper.contact_person ||
          selectedShipper.phone_number || selectedShipper.email_address)
      );
    }
  };

  const handleDeleteShipper = (shipper: ShipperEntity) => {
    if (!window.confirm(`${shipper.name} を補完候補から削除します。よろしいですか？`)) return;
    deleteShipper.mutate(shipper.id);
  };

  const renderShipperOption = (
    props: HTMLAttributes<HTMLLIElement>,
    option: ShipperEntity
  ) => {
    const optionProps = { ...props } as HTMLAttributes<HTMLLIElement> & { key?: Key };
    const optionKey = optionProps.key;
    delete optionProps.key;

    return (
      <li key={optionKey} {...optionProps}>
        <ShipperOption option={option} disabled={deleteShipper.isPending} onDelete={handleDeleteShipper} />
      </li>
    );
  };

  // 地点選択時の共通処理
  const handleLocationChange = (
    type: 'pickup' | 'delivery',
    value: string | PlaceEntity | null
  ) => {
    const locationField = type === 'pickup' ? 'pickupLocation' : 'deliveryLocation';
    const addressField = type === 'pickup' ? 'pickupAddress' : 'deliveryAddress';
    const latitudeField = type === 'pickup' ? 'pickupLatitude' : 'deliveryLatitude';
    const longitudeField = type === 'pickup' ? 'pickupLongitude' : 'deliveryLongitude';
    const skipGeocodingField = type === 'pickup' ? 'skipPickupGeocoding' : 'skipDeliveryGeocoding';
    const timeSlotField = type === 'pickup' ? 'pickupTimeSlot' : 'deliveryTimeSlot';
    const durationField = type === 'pickup' ? 'pickupDuration' : 'deliveryDuration';

    if (!value) {
      setValue(locationField, '', { shouldDirty: true });
      setValue(skipGeocodingField, false, { shouldDirty: true });
      return;
    }

    let selectedPlace: PlaceEntity | undefined;
    if (typeof value === 'string') {
      setValue(locationField, value, { shouldDirty: true });
      setValue(skipGeocodingField, false, { shouldDirty: true });
      selectedPlace = places.find((place) => place.name === value);
    } else {
      selectedPlace = value;
    }

    if (selectedPlace) {
      setValue(locationField, selectedPlace.name, { shouldDirty: true });
      setValue(addressField, selectedPlace.address || '', { shouldDirty: true });
      setValue(latitudeField, selectedPlace.latitude || undefined, { shouldDirty: true });
      setValue(longitudeField, selectedPlace.longitude || undefined, { shouldDirty: true });
      setValue(skipGeocodingField, true, { shouldDirty: true });
      setValue(timeSlotField, 'カスタム', { shouldDirty: true });

      // 地点マスタの指定時間情報をカスタム時間に設定
      if (selectedPlace.place_specified_times_attributes &&
          selectedPlace.place_specified_times_attributes.length > 0) {
        const currentDate = type === 'pickup' ? watch('pickupDate') : watch('deliveryDate');
        const customTimes = selectedPlace.place_specified_times_attributes.map(timeAttr => ({
          date: currentDate,
          startTime: timeAttr.start_at || '',
          endTime: timeAttr.end_at || ''
        }));

        // useFieldArrayのフィールドを更新
        if (type === 'pickup') {
          // 既存のフィールドをクリアして新しいカスタム時間を設定
          const currentLength = pickupCustomTimeFields.length;
          for (let i = currentLength - 1; i >= 0; i--) {
            removePickupCustomTimeField(i);
          }
          customTimes.forEach(time => appendPickupCustomTime(time));
        } else {
          // 既存のフィールドをクリアして新しいカスタム時間を設定
          const currentLength = deliveryCustomTimeFields.length;
          for (let i = currentLength - 1; i >= 0; i--) {
            removeDeliveryCustomTimeField(i);
          }
          customTimes.forEach(time => appendDeliveryCustomTime(time));
        }
      } else {
        // 指定時間情報がない場合はデフォルト値を設定
        const currentDate = type === 'pickup' ? watch('pickupDate') : watch('deliveryDate');
        if (type === 'pickup') {
          const currentLength = pickupCustomTimeFields.length;
          for (let i = currentLength - 1; i >= 0; i--) {
            removePickupCustomTimeField(i);
          }
          appendPickupCustomTime({ date: currentDate, startTime: "", endTime: "" });
        } else {
          const currentLength = deliveryCustomTimeFields.length;
          for (let i = currentLength - 1; i >= 0; i--) {
            removeDeliveryCustomTimeField(i);
          }
          appendDeliveryCustomTime({ date: currentDate, startTime: "", endTime: "" });
        }
      }
      setValue(durationField, selectedPlace.staying_minutes || 0, { shouldDirty: true });
    }
  };

  const resetProductDetail = (index: number) => {
    setValue(`shipmentOrderItems.${index}.productCode`, null, { shouldDirty: true });
    setValue(`shipmentOrderItems.${index}.productName`, null, { shouldDirty: true });
    setValue(`shipmentOrderItems.${index}.entity`, null, { shouldDirty: true });
  };

  const updateProductDetail = (index: number, value: ProductEntity) => {
    setValue(`shipmentOrderItems.${index}.productCode`, value.code, { shouldDirty: true });
    setValue(`shipmentOrderItems.${index}.productName`, value.name, { shouldDirty: true });
    setValue(`shipmentOrderItems.${index}.entity`, value, { shouldDirty: true });
    applyAutoFill(index);
  };

  const applyAutoFill = (index: number) => {
    const item = getValues(`shipmentOrderItems.${index}`);
    const result = computeAutoFillItemWeightVolume({
      entity: item.entity,
      quantity: item.quantity,
      weightKgManuallyEdited: item.weightKgManuallyEdited,
      volumeM3ManuallyEdited: item.volumeM3ManuallyEdited,
    });
    if (result.weightKg !== undefined) {
      setValue(`shipmentOrderItems.${index}.weight_kg`, result.weightKg, { shouldDirty: true });
    }
    if (result.volumeM3 !== undefined) {
      setValue(`shipmentOrderItems.${index}.volume_m3`, result.volumeM3, { shouldDirty: true });
    }
  };

  const handleProductCodeChange = (value: string | ProductEntity | null, index: number) => {
    if (!value) {
      resetProductDetail(index);
      return;
    }

    let selectedProduct: ProductEntity | undefined;
    if (typeof value === 'string') {
      updateProduct(index, 'productCode', value);
      selectedProduct = products.find((product) => product.code === value);
    } else {
      selectedProduct = value;
    }

    if (selectedProduct) {
      updateProductDetail(index, selectedProduct);
    } else {
      setValue(`shipmentOrderItems.${index}.entity`, null, { shouldDirty: true });
    }
  };

  const handleProductNameChange = (value: string | ProductEntity | null, index: number) => {
    if (!value) {
      resetProductDetail(index);
      return;
    }

    let selectedProduct: ProductEntity | undefined;
    if (typeof value === 'string') {
      updateProduct(index, 'productName', value);
      selectedProduct = products.find((product) => product.name === value);
    } else {
      selectedProduct = value;
    }

    if (selectedProduct) {
      updateProductDetail(index, selectedProduct);
    } else {
      setValue(`shipmentOrderItems.${index}.entity`, null, { shouldDirty: true });
    }
  };

  return (
    <Dialog
      open={newShipmentDialogOpen}
      onClose={handleCancel}
      maxWidth="lg"
      fullWidth
      sx={{ pb: 10 }}
    >
      <DialogTitle>新規伝票作成</DialogTitle>
      <DialogContent>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ja}>
          <Box sx={{ maxWidth: '1400px', margin: '0 auto', }}>
            {/* ヘッダー情報 */}
            <Card sx={{ mb: 3, bgcolor: 'primary.50', borderColor: 'primary.200' }}>
              <CardHeader
                title={<Typography variant="h6" color="primary">システム情報</Typography>}
                sx={{ py: 1 }}
              />
              <CardContent sx={{ py: 1 }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
                  <TextField
                    label="伝票No"
                    value={watch("code") || ""}
                    onChange={(e) => setValue("code", e.target.value)}
                    placeholder="空白の場合は自動採番"
                    fullWidth
                    {...register("code")}
                  />
                  <TextField
                    label="案件登録者"
                    value={registrationUser}
                    disabled
                    fullWidth
                  />
                </Box>
              </CardContent>
            </Card>

            {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
            <Box component="form" id="shipment-form" onSubmit={handleSubmit(handleFormSubmit)} sx={{ pb: 10 }}>
              {/* ① 基本情報 */}
              <Card sx={{ mb: 3 }}>
                <CardHeader
                  title={<Typography variant="h6" color="primary">① 基本情報</Typography>}
                  sx={{ py: 1 }}
                />
                <CardContent sx={{ py: 1 }}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3, mb: 3 }}>
                    {/* 荷主名（必須項目） */}
                    <Controller
                      name="shipperName"
                      control={control}
                      rules={{ required: "荷主名は必須です" }}
                      render={({ field, fieldState: { error } }) => (
                        <Autocomplete
                          {...field}
                          options={shippers}
                          getOptionLabel={(option) => typeof option === "string" ? option : option.name}
                          freeSolo
                          loading={shippersQuery.isLoading || deleteShipper.isPending}
                          value={field.value || null}
                          renderOption={renderShipperOption}
                          onChange={(_, value) => {
                            const newValue = typeof value === 'string' ? value: value?.name ?? '';
                            field.onChange(newValue);
                            handleShipperChange(_, value);
                          }}
                          onInputChange={(_, inputValue, reason) => {
                            if (reason === 'input') {
                              field.onChange(inputValue);
                              handleShipperChange(_, inputValue);
                            }
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="荷主名"
                              placeholder="荷主を選択または入力"
                              required
                              error={!!error}
                              helperText={error ? error.message : null}
                              fullWidth
                            />
                          )}
                        />
                      )}
                    />
                    {/* 配送形態 */}
                    <FormControl fullWidth>
                      <InputLabel>配送形態</InputLabel>
                      <MuiSelect
                        label="配送形態"
                        value={watch("deliveryMethod") || ""}
                        onChange={(e) => setValue("deliveryMethod", e.target.value)}
                      >
                        <MenuItem value="貸切">貸切</MenuItem>
                        <MenuItem value="共配">共配</MenuItem>
                        <MenuItem value="路線便">路線便</MenuItem>
                      </MuiSelect>
                    </FormControl>
                  </Box>

                  {/* 展開可能な荷主情報 */}
                  <Box sx={{ mb: 3 }}>
                    <MuiButton
                      variant="outlined"
                      size="small"
                      onClick={() => setShipperInfoExpanded(!shipperInfoExpanded)}
                      sx={{ mb: 2 }}
                    >
                      {shipperInfoExpanded ? "荷主詳細情報を隠す" : "荷主詳細情報を表示"}
                    </MuiButton>

                    {shipperInfoExpanded && (
                      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
                        <TextField
                          label="荷主担当者"
                          placeholder="担当者名を入力"
                          fullWidth
                          value={watch("shipperContactPerson") || ""}
                          slotProps={{
                            inputLabel: {
                              shrink: Boolean(watch("shipperContactPerson"))
                            }
                          }}
                          {...register("shipperContactPerson")}
                        />
                        <TextField
                          label="連絡先（電話）"
                          placeholder="03-1234-5678"
                          fullWidth
                          value={watch("shipperPhoneNumber") || ""}
                          slotProps={{
                            inputLabel: {
                              shrink: Boolean(watch("shipperPhoneNumber"))
                            }
                          }}
                          {...register("shipperPhoneNumber")}
                        />
                        <TextField
                          label="連絡先（メール）"
                          placeholder="contact@example.com"
                          fullWidth
                          value={watch("shipperEmailAddress") || ""}
                          slotProps={{
                            inputLabel: {
                              shrink: Boolean(watch("shipperEmailAddress"))
                            }
                          }}
                          {...register("shipperEmailAddress")}
                        />
                      </Box>
                    )}
                  </Box>
                  <TextField
                    label="備考"
                    multiline
                    rows={3}
                    placeholder="特記事項があれば入力してください"
                    fullWidth
                    {...register("memo")}
                  />
                </CardContent>
              </Card>

              {/* ② 地点情報 */}
              <Card sx={{ mb: 3 }}>
                <CardHeader
                  title={<Typography variant="h6" color="primary">② 地点情報</Typography>}
                  sx={{ py: 1 }}
                />
                <CardContent sx={{ py: 1 }}>
                  {/* 区間種別 */}
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="subtitle1" color="primary" sx={{ mb: 1, }} gutterBottom>区間種別</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
                      {sectionTypes.map((sectionType, index) => {
                        const getRouteDescription = () => {
                          if (intermediateLocationFields.length === 0) return "積地 → 降地";
                          if (intermediateLocationFields.length === 1) {
                            return index === 0 ? "積地 → 中継地" : "中継地 → 降地";
                          }
                          if (intermediateLocationFields.length === 2) {
                            if (index === 0) return "積地 → 中継地1";
                            if (index === 1) return "中継地1 → 中継地2";
                            return "中継地2 → 降地";
                          }
                        };

                        return (
                          <Box key={index} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', }}>
                            <FormControl sx={{ minWidth: 120, mb: 1 }} size="small">
                              <InputLabel>区間{index + 1}</InputLabel>
                              <MuiSelect
                                value={sectionType}
                                label={`区間${index + 1}`}
                                onChange={(e) => updateSectionType(index, e.target.value)}
                              >
                                <MenuItem value="配送">配送</MenuItem>
                                <MenuItem value="集荷">集荷</MenuItem>
                                <MenuItem value="横持ち">横持ち</MenuItem>
                              </MuiSelect>
                            </FormControl>
                            <Typography variant="caption" color="text.secondary" textAlign="center">
                              {getRouteDescription()}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>

                  {/* 積地 */}
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="subtitle1" color="primary" gutterBottom>
                      積地（必須）
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3, mb: 2 }}>
                      <Controller
                        name="pickupLocation"
                        control={control}
                        rules={{ required: "積地名称は必須です" }}
                        render={({ field, fieldState: { error } }) => (
                          <Autocomplete
                            {...field}
                            options={pickupPlaces}
                            getOptionLabel={(option) => typeof option === "string" ? option : option.name}
                            freeSolo
                            loading={placesQuery.isLoading}
                            value={field.value || null}
                            onChange={(_, value) => {
                              field.onChange(value);
                              handleLocationChange('pickup', value);
                            }}
                            onInputChange={(_, inputValue, reason) => {
                              if (reason === 'input') {
                                field.onChange(inputValue);
                              handleLocationChange('pickup', inputValue);
                              }
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="名称"
                                placeholder="地点マスタから選択または入力"
                                required
                                error={!!error}
                                helperText={error?.message}
                              />
                            )}
                          />
                        )}
                      />
                      <TextField
                        label="住所"
                        placeholder="住所"
                        fullWidth
                        value={watch("pickupAddress") || ""}
                        slotProps={{
                          inputLabel: {
                            shrink: Boolean(watch("pickupAddress"))}
                        }}
                        {...register("pickupAddress")}
                        onChange={(e) => {
                          setValue("pickupAddress", e.target.value, { shouldDirty: true });
                          setValue("skipPickupGeocoding", false, { shouldDirty: true });
                        }}
                      />
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 3 }}>
                      <DatePicker
                        label="積日"
                        value={watch("pickupDate")}
                        onChange={(date) => setValue("pickupDate", date || new Date())}
                        format="yyyy/MM/dd"
                        slotProps={{ textField: { fullWidth: true, required: true } }}
                      />
                      <FormControl fullWidth>
                        <InputLabel>時間枠</InputLabel>
                        <MuiSelect
                          label="時間枠"
                          value={watch("pickupTimeSlot") || "AM"}
                          onChange={(e) => setValue("pickupTimeSlot", e.target.value)}
                        >
                          {timeSlotOptions.map((option) => (
                            <MenuItem key={option} value={option}>{option}</MenuItem>
                          ))}
                        </MuiSelect>
                      </FormControl>
                      <TextField
                        label="滞在時間（分）"
                        type="number"
                        placeholder="30"
                        fullWidth
                        value={watch("pickupDuration") || ""}
                        slotProps={{ inputLabel: { shrink: Boolean(watch("pickupDuration")) } }}
                        {...register("pickupDuration", { valueAsNumber: true })}
                      />
                    </Box>
                    {watch("pickupTimeSlot") === "カスタム" && (
                      <Box sx={{ mt: 2 }}>
                        <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                          <Typography variant="subtitle2" gutterBottom>カスタム時間設定</Typography>
                          <IconButton
                            size="small"
                            onClick={() => addCustomTime('pickup')}
                            color="primary"
                            disabled={pickupCustomTimeFields.length >= 3}
                          >
                            <AddIcon />
                          </IconButton>
                        </Stack>
                        {pickupCustomTimeFields.map((field, index) => (
                          <Box key={field.id} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                            <Controller
                              name={`pickupCustomTimes.${index}.date`}
                              control={control}
                              defaultValue={watch("pickupDate")}
                              render={({ field }) => (
                                <DatePicker
                                  label="日付"
                                  value={field.value ?? watch("pickupDate")}
                                  onChange={(date) => {
                                    field.onChange(date ?? watch("pickupDate"));
                                  }}
                                  format="yyyy/MM/dd"
                                  slotProps={{ textField: { size: "small", sx: { flex: 1 } } }}
                                />
                              )}
                            />
                            <Controller
                              name={`pickupCustomTimes.${index}.startTime`}
                              control={control}
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  type="time"
                                  placeholder="09:00"
                                  label="開始時間"
                                  size="small"
                                  sx={{ flex: 1 }}
                                  slotProps={{ inputLabel: { shrink: true } }}
                                  onChange={(e) => {
                                    field.onChange(e);
                                    updateCustomTime('pickup', index, 'startTime', e.target.value);
                                  }}
                                />
                              )}
                            />
                            <Controller
                              name={`pickupCustomTimes.${index}.endTime`}
                              control={control}
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  type="time"
                                  placeholder="17:00"
                                  label="終了時間"
                                  size="small"
                                  sx={{ flex: 1 }}
                                  slotProps={{ inputLabel: { shrink: true } }}
                                  onChange={(e) => {
                                    field.onChange(e);
                                    updateCustomTime('pickup', index, 'endTime', e.target.value);
                                  }}
                                />
                              )}
                            />
                            <IconButton
                              size="small"
                              onClick={() => removeCustomTime('pickup', index)}
                              color="error"
                              disabled={pickupCustomTimeFields.length <= 1}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>

                  {/* 中継地 */}
                  {/* <Box sx={{ mb: 4 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="subtitle1" color="primary">
                        中継地（任意）
                      </Typography>
                      <MuiButton
                        variant="outlined"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={addIntermediateLocation}
                        disabled={intermediateLocationFields.length >= 2}
                      >
                        中継地追加 ({intermediateLocationFields.length}/2)
                      </MuiButton>
                    </Box>
                    {intermediateLocationFields.map((location, index) => (
                      <Box key={index} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 2, mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Typography variant="subtitle2">中継地 {index + 1}</Typography>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => removeIntermediateLocation(index)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                          <Autocomplete
                            options={places}
                            getOptionLabel={(option) => typeof option === "string" ? option : option.name}
                            freeSolo
                            loading={placesQuery.isLoading}
                            value={location.name}
                            onChange={(_, value) => updateIntermediateLocation(index, "name", value || "")}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="名称"
                                placeholder="地点マスタから選択または入力"
                                size="small"
                                {...register(`intermediateLocations.${index}.name` as const)}
                              />
                            )}
                          />
                          <TextField
                            label="住所"
                            placeholder="住所"
                            value={location.address}
                            onChange={(e) => updateIntermediateLocation(index, "address", e.target.value)}
                            size="small"
                            {...register(`intermediateLocations.${index}.address` as const)}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                          <Chip
                            label="当日積"
                            variant={location.dayPickup ? "filled" : "outlined"}
                            color={location.dayPickup ? "primary" : "default"}
                            onClick={() => updateIntermediateLocation(index, "dayPickup", !location.dayPickup)}
                            clickable
                          />
                          <Chip
                            label="宵積み"
                            variant={location.eveningPickup ? "filled" : "outlined"}
                            color={location.eveningPickup ? "primary" : "default"}
                            onClick={() => updateIntermediateLocation(index, "eveningPickup", !location.eveningPickup)}
                            clickable
                          />
                        </Box>
                      </Box>
                    ))}
                  </Box> */}

                  {/* 降地 */}
                  <Box>
                    <Typography variant="subtitle1" color="primary" gutterBottom>
                      降地（必須）
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3, mb: 2 }}>
                      <Controller
                        name="deliveryLocation"
                        control={control}
                        rules={{ required: "降地名称は必須です" }}
                        render={({ field, fieldState: { error } }) => (
                          <Autocomplete
                            {...field}
                            options={deliveryPlaces}
                            getOptionLabel={(option) => typeof option === "string" ? option : option.name}
                            freeSolo
                            loading={placesQuery.isLoading}
                            value={field.value || null}
                            onChange={(_, value) => {
                              field.onChange(value);
                              handleLocationChange('delivery', value);
                            }}
                            onInputChange={(_, inputValue, reason) => {
                              if (reason === 'input') {
                                field.onChange(inputValue);
                              handleLocationChange('delivery', inputValue);
                              }
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="名称"
                                placeholder="地点マスタから選択または入力"
                                required
                                error={!!error}
                                helperText={error?.message}
                              />
                            )}
                          />
                        )}
                      />
                      <TextField
                        label="住所"
                        placeholder="住所"
                        fullWidth
                        value={watch("deliveryAddress") || ""}
                        slotProps={{ inputLabel: { shrink: Boolean(watch("deliveryAddress")) }}}
                        {...register("deliveryAddress")}
                        onChange={(e) => {
                          setValue("deliveryAddress", e.target.value, { shouldDirty: true });
                          setValue("skipDeliveryGeocoding", false, { shouldDirty: true });
                        }}
                      />
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 3 }}>
                      <DatePicker
                        label="降日"
                        value={watch("deliveryDate")}
                        onChange={(date) => setValue("deliveryDate", date || new Date())}
                        format="yyyy/MM/dd"
                        slotProps={{ textField: { fullWidth: true, required: true } }}
                      />
                      <FormControl fullWidth>
                        <InputLabel>時間枠</InputLabel>
                        <MuiSelect
                          label="時間枠"
                          value={watch("deliveryTimeSlot") || "AM"}
                          onChange={(e) => setValue("deliveryTimeSlot", e.target.value)}
                        >
                          {timeSlotOptions.map((option) => (
                            <MenuItem key={option} value={option}>{option}</MenuItem>
                          ))}
                        </MuiSelect>
                      </FormControl>
                      <TextField
                        label="滞在時間（分）"
                        type="number"
                        placeholder="30"
                        fullWidth
                        value={watch("deliveryDuration") || ""}
                        slotProps={{ inputLabel: { shrink: Boolean(watch("deliveryDuration")) } }}
                        {...register("deliveryDuration", { valueAsNumber: true })}
                      />
                    </Box>
                    {watch("deliveryTimeSlot") === "カスタム" && (
                      <Box sx={{ mt: 2 }}>
                        <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                          <Typography variant="subtitle2" gutterBottom>カスタム時間設定</Typography>
                          <IconButton
                            size="small"
                            onClick={() => addCustomTime('delivery')}
                            color="primary"
                            disabled={deliveryCustomTimeFields.length >= 3}
                          >
                            <AddIcon />
                          </IconButton>
                        </Stack>
                        {deliveryCustomTimeFields.map((field, index) => (
                          <Box key={field.id} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                            <Controller
                              name={`deliveryCustomTimes.${index}.date`}
                              control={control}
                              defaultValue={watch("deliveryDate")}
                              render={({ field }) => (
                                <DatePicker
                                  label="日付"
                                  value={field.value ?? watch("deliveryDate")}
                                  onChange={(date) => {
                                    field.onChange(date ?? watch("deliveryDate"));
                                  }}
                                  format="yyyy/MM/dd"
                                  slotProps={{ textField: { size: "small", sx: { flex: 1 } } }}
                                />
                              )}
                            />
                            <Controller
                              name={`deliveryCustomTimes.${index}.startTime`}
                              control={control}
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  type="time"
                                  placeholder="09:00"
                                  label="開始時間"
                                  size="small"
                                  sx={{ flex: 1 }}
                                  slotProps={{ inputLabel: { shrink: true } }}
                                  onChange={(e) => {
                                    field.onChange(e);
                                    updateCustomTime('delivery', index, 'startTime', e.target.value);
                                  }}
                                />
                              )}
                            />
                            <Controller
                              name={`deliveryCustomTimes.${index}.endTime`}
                              control={control}
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  type="time"
                                  placeholder="17:00"
                                  label="終了時間"
                                  size="small"
                                  sx={{ flex: 1 }}
                                  slotProps={{ inputLabel: { shrink: true } }}
                                  onChange={(e) => {
                                    field.onChange(e);
                                    updateCustomTime('delivery', index, 'endTime', e.target.value);
                                  }}
                                />
                              )}
                            />
                            <IconButton
                              size="small"
                              onClick={() => removeCustomTime('delivery', index)}
                              color="error"
                              disabled={deliveryCustomTimeFields.length <= 1}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>

                  {/* 貨物条件 */}
                  <Box sx={{ mt: 4 }}>
                    <Typography variant="subtitle1" color="primary" gutterBottom>
                      貨物条件
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3, mb: 2 }}>
                      <FormControl fullWidth>
                        <InputLabel>貨物種別</InputLabel>
                        <MuiSelect
                          label="貨物種別"
                          value={watch("itemKlass") || ""}
                          onChange={(e) => setValue("itemKlass", e.target.value)}
                        >
                          <MenuItem value="">&nbsp;</MenuItem>
                          {ItemKlassOptions.map((option) => (
                            <MenuItem key={option} value={option}>{option}</MenuItem>
                          ))}
                        </MuiSelect>
                      </FormControl>
                      <FormControl fullWidth>
                        <InputLabel>荷扱い</InputLabel>
                        <MuiSelect
                          label="荷扱い"
                          value={watch("itemHandlingOfCargoStyle") || ""}
                          onChange={(e) => setValue("itemHandlingOfCargoStyle", e.target.value)}
                        >
                          <MenuItem value="">&nbsp;</MenuItem>
                          {ItemHandlingOfCargoStyleOptions.map((option) => (
                            <MenuItem key={option} value={option}>{option}</MenuItem>
                          ))}
                        </MuiSelect>
                      </FormControl>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={watch("itemCanBeMixed") ?? true}
                            onChange={(e) => setValue("itemCanBeMixed", e.target.checked)}
                            color="primary"
                          />
                        }
                        label="積み合わせ可否"
                      />
                      <Tooltip
                        title="積み合わせ不可の場合は、同じ荷主・同じ貨物種別の荷物としか運べません"
                        arrow
                      >
                        <HelpOutlineIcon sx={{ color: 'grey.500', fontSize: 18, cursor: 'help' }} />
                      </Tooltip>
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              {/* ③ 商品情報 */}
              <Card sx={{ mb: 3 }}>
                <CardHeader
                  title={<Typography variant="h6" color="primary">③ 商品情報</Typography>}
                  sx={{ py: 1 }}
                />
                <CardContent sx={{ py: 1 }}>
                  <TableContainer component={Paper} sx={{ width: '100%' }}>
                    <Table sx={{ width: '100%', tableLayout: 'fixed' }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ width: '12%' }}>商品コード</TableCell>
                          <TableCell sx={{ width: '20%' }}>品名</TableCell>
                          <TableCell sx={{ width: '12%' }}>荷姿</TableCell>
                          <TableCell sx={{ width: '10%' }}>数量</TableCell>
                          <TableCell sx={{ width: '10%' }}>重量</TableCell>
                          <TableCell sx={{ width: '10%' }}>体積</TableCell>
                          <TableCell sx={{ width: '13%' }}>ロットNo</TableCell>
                          <TableCell sx={{ width: '13%' }}>特注No</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {itemFields.map((product, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              <Controller
                                name={`shipmentOrderItems.${index}.productCode`}
                                control={control}
                                render={({ field, fieldState: { error } }) => (
                                  <Autocomplete
                                    {...field}
                                    options={products.filter((p) => p.code !== null)}
                                    getOptionLabel={(option) => typeof option === "string" ? option : option.code}
                                    getOptionKey={(option) => typeof option === 'string' ? option : option.id}
                                    freeSolo
                                    loading={productsRequest.isLoading}
                                    value={field.value || null}
                                    onInputChange={(_, value) => {
                                      if (value) {
                                        updateProduct(index, "productCode", value);
                                      }
                                      // タイプ入力で entity と乖離したら自動補完を抑止するために entity を null に戻す。
                                      // 候補に再一致したときは onChange で再セットされる。
                                      const currentEntity = getValues(`shipmentOrderItems.${index}.entity`);
                                      if (currentEntity && currentEntity.code !== value) {
                                        setValue(`shipmentOrderItems.${index}.entity`, null, { shouldDirty: true });
                                      }
                                    }}
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        placeholder="商品コード"
                                        size="small"
                                        fullWidth
                                        error={!!error}
                                        helperText={error?.message}
                                      />
                                    )}
                                    onChange={(_, value) => handleProductCodeChange(value, index)}
                                    slotProps={{
                                      clearIndicator: {
                                        sx: { m: '-8px', }
                                      }
                                    }}
                                  />
                                )}
                              />
                            </TableCell>
                            <TableCell>
                              <Controller
                                name={`shipmentOrderItems.${index}.productName`}
                                control={control}
                                render={({ field, fieldState: { error } }) => (
                                  <Autocomplete
                                    {...field}
                                    options={products.filter((p) => p.name !== null)}
                                    getOptionLabel={(option) => typeof option === "string" ? option : option.name}
                                    getOptionKey={(option) => typeof option === 'string' ? option : option.id}
                                    freeSolo
                                    loading={productsRequest.isLoading}
                                    value={field.value || null}
                                    onInputChange={(_, value) => {
                                      if (value) {
                                        updateProduct(index, "productName", value);
                                      }
                                      // タイプ入力で entity と乖離したら自動補完を抑止するために entity を null に戻す。
                                      const currentEntity = getValues(`shipmentOrderItems.${index}.entity`);
                                      if (currentEntity && currentEntity.name !== value) {
                                        setValue(`shipmentOrderItems.${index}.entity`, null, { shouldDirty: true });
                                      }
                                    }}
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        placeholder="品名"
                                        size="small"
                                        fullWidth
                                        error={!!error}
                                        helperText={error?.message}
                                      />
                                    )}
                                    onChange={(_, value) => handleProductNameChange(value, index)}
                                    slotProps={{
                                      clearIndicator: {
                                        sx: { m: '-8px', }
                                      }
                                    }}
                                  />
                                )}
                              />
                            </TableCell>
                            <TableCell>
                              <Controller
                                name={`shipmentOrderItems.${index}.itemPackingStyle`}
                                control={control}
                                render={({ field }) => (
                                  <Autocomplete
                                    freeSolo
                                    size="small"
                                    options={[...ItemPackageStyleOptions]}
                                    value={field.value || null}
                                    onChange={(_, newValue) => field.onChange(newValue ?? '')}
                                    onInputChange={(_, newValue) => field.onChange(newValue)}
                                    componentsProps={{ popper: { style: { width: 'fit-content' } } }}
                                    renderInput={(params) => <TextField {...params} size="small" />}
                                  />
                                )}
                              />
                            </TableCell>
                            <TableCell>
                              <Controller
                                name={`shipmentOrderItems.${index}.quantity`}
                                control={control}
                                render={({ field }) => (
                                  <TextField
                                    {...field}
                                    type="number"
                                    value={field.value ?? ''}
                                    onChange={(e) => {
                                      const v = e.target.value === '' ? '' : (parseInt(e.target.value) || 0);
                                      field.onChange(v);
                                      applyAutoFill(index);
                                    }}
                                    placeholder="数量"
                                    size="small"
                                    fullWidth
                                  />
                                )}
                              />
                            </TableCell>
                            <TableCell>
                              <Controller
                                name={`shipmentOrderItems.${index}.weight_kg`}
                                control={control}
                                render={({ field }) => (
                                  <TextField
                                    {...field}
                                    type="number"
                                    value={field.value ?? ''}
                                    onChange={(e) => {
                                      const v = e.target.value === '' ? '' : (parseFloat(e.target.value) || 0);
                                      field.onChange(v);
                                      // 値が入っているとき true、空にしたら false に戻る。
                                      // = ユーザーが手動入力した値は自動補完で上書きしないが、
                                      //   一度空にすれば再び自動補完を有効化する仕様 (設計書参照)。
                                      setValue(
                                        `shipmentOrderItems.${index}.weightKgManuallyEdited`,
                                        v !== '',
                                        { shouldDirty: false },
                                      );
                                    }}
                                    placeholder="重量"
                                    size="small"
                                    fullWidth
                                  />
                                )}
                              />
                            </TableCell>
                            <TableCell>
                              <Controller
                                name={`shipmentOrderItems.${index}.volume_m3`}
                                control={control}
                                render={({ field }) => (
                                  <TextField
                                    {...field}
                                    type="number"
                                    value={field.value ?? ''}
                                    onChange={(e) => {
                                      const v = e.target.value === '' ? '' : (parseFloat(e.target.value) || 0);
                                      field.onChange(v);
                                      // 値が入っているとき true、空にしたら false に戻る。
                                      // = ユーザーが手動入力した値は自動補完で上書きしないが、
                                      //   一度空にすれば再び自動補完を有効化する仕様 (設計書参照)。
                                      setValue(
                                        `shipmentOrderItems.${index}.volumeM3ManuallyEdited`,
                                        v !== '',
                                        { shouldDirty: false },
                                      );
                                    }}
                                    placeholder="体積"
                                    size="small"
                                    fullWidth
                                  />
                                )}
                              />
                            </TableCell>
                            <TableCell>
                              <Controller
                                name={`shipmentOrderItems.${index}.lotNumber`}
                                control={control}
                                render={({ field }) => (
                                  <TextField
                                    {...field}
                                    value={field.value || ''}
                                    onChange={(e) => {
                                      field.onChange(e.target.value);
                                      updateProduct(index, "lotNumber", e.target.value);
                                    }}
                                    placeholder="ロットNo"
                                    size="small"
                                    fullWidth
                                  />
                                )}
                              />
                            </TableCell>
                            <TableCell>
                              <Controller
                                name={`shipmentOrderItems.${index}.customOrderNumber`}
                                control={control}
                                render={({ field }) => (
                                  <TextField
                                    {...field}
                                    value={field.value || ''}
                                    onChange={(e) => {
                                      field.onChange(e.target.value);
                                      updateProduct(index, "customOrderNumber", e.target.value);
                                    }}
                                    placeholder="特注No"
                                    size="small"
                                    fullWidth
                                  />
                                )}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                    <MuiButton
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={addProduct}
                      size="small"
                    >
                      商品行追加
                    </MuiButton>
                    {itemFields.length > 1 && (
                      <MuiButton
                        variant="outlined"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={() => removeProduct(itemFields.length - 1)}
                        size="small"
                      >
                        最終行削除
                      </MuiButton>
                    )}
                  </Box>
                </CardContent>
              </Card>

              {/* ④ 任意項目情報 */}
              <Card sx={{ mb: 3 }}>
                <CardHeader
                  title={<Typography variant="h6" color="primary">④ 任意項目情報</Typography>}
                  sx={{ py: 1 }}
                />
                <CardContent sx={{ py: 1 }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                    {customInputFields.map((it, index) => (
                      <TextField
                        key={index}
                        label={it}
                        fullWidth
                        multiline
                        rows={2}
                        sx={{ mb: 2 }}
                        {...register(`customInputFields.${index}.value`)}
                        onChange={(e) => {
                          setValue(`customInputFields.${index}.key`, it, { shouldDirty: true });
                          setValue(`customInputFields.${index}.value`, e.target.value, { shouldDirty: true });
                        }}
                      />
                    ))}
                  </Stack>
                </CardContent>
              </Card>

              {/* ⑤ 条件・請求 */}
              <Card sx={{ mb: 3 }}>
                <CardHeader
                  title={<Typography variant="h6" color="primary">⑤ 条件・請求</Typography>}
                  sx={{ py: 1 }}
                />
                <CardContent sx={{ py: 1 }}>
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography>車両指定条件（詳細設定）</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box>
                        {/* 指定車両タイプ */}
                        <Box sx={{ mb: 2, }}>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>指定車両タイプ</Typography>
                          <Box display="flex" flexDirection="row" gap={1}>
                            {DesignatedTruckKlassOptions.map((type) => (
                              <FormControlLabel
                                key={type}
                                control={
                                  <Checkbox
                                    checked={(watch("designatedTruckKlasses") || []).includes(type)}
                                    onChange={(e) => {
                                      const currentTypes = watch("designatedTruckKlasses") || [];
                                      const newTypes = e.target.checked
                                        ? [...currentTypes, type]
                                        : currentTypes.filter(t => t !== type);
                                      setValue("designatedTruckKlasses", newTypes, { shouldDirty: true });
                                    }}
                                  />
                                }
                                label={type}
                              />
                            ))}
                          </Box>
                        </Box>

                        {/* 指定車種 */}
                        <Box sx={{ mb: 2, }}>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>指定車種</Typography>
                          <Box display="flex" flexWrap="wrap" gap={1}>
                            {DesignatedTruckCarModelOptions.map((model) => (
                              <FormControlLabel
                                key={model}
                                control={
                                  <Checkbox
                                    checked={(watch("designatedTruckCarModels") || []).includes(model)}
                                    onChange={(e) => {
                                      const currentModels = watch("designatedTruckCarModels") || [];
                                      const newModels = e.target.checked
                                        ? [...currentModels, model]
                                        : currentModels.filter(m => m !== model);
                                      setValue("designatedTruckCarModels", newModels, { shouldDirty: true });
                                    }}
                                  />
                                }
                                label={model}
                                sx={{
                                  whiteSpace: 'nowrap',
                                  minWidth: 'fit-content',
                                  flexShrink: 0,
                                  mb: 0,
                                }}
                              />
                            ))}
                          </Box>
                        </Box>

                        {/* 荷台高さ */}
                        <Box sx={{ mb: 2, }}>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>荷台高さ</Typography>
                          <Box display="flex" flexDirection="row" gap={1}>
                            {DesignatedTruckLoadingPlatformHeightOptions.map((height) => (
                              <FormControlLabel
                                key={height}
                                control={
                                  <Checkbox
                                    checked={(watch("designatedTruckLoadingPlatformHeights") || []).includes(height)}
                                    onChange={(e) => {
                                      const currentHeights = watch("designatedTruckLoadingPlatformHeights") || [];
                                      const newHeights = e.target.checked
                                        ? [...currentHeights, height]
                                        : currentHeights.filter(h => h !== height);
                                      setValue("designatedTruckLoadingPlatformHeights", newHeights, { shouldDirty: true });
                                    }}
                                  />
                                }
                                label={height}
                              />
                            ))}
                          </Box>
                        </Box>

                        {/* 荷台幅 */}
                        <Box sx={{ mb: 2, }}>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>荷台幅</Typography>
                          <Box display="flex" flexDirection="row" gap={1}>
                            {DesignatedTruckLoadingPlatformWidthOptions.map((width) => (
                              <FormControlLabel
                                key={width}
                                control={
                                  <Checkbox
                                    checked={(watch("designatedTruckLoadingPlatformWidths") || []).includes(width)}
                                    onChange={(e) => {
                                      const currentWidths = watch("designatedTruckLoadingPlatformWidths") || [];
                                      const newWidths = e.target.checked
                                        ? [...currentWidths, width]
                                        : currentWidths.filter(w => w !== width);
                                      setValue("designatedTruckLoadingPlatformWidths", newWidths, { shouldDirty: true });
                                    }}
                                  />
                                }
                                label={width}
                              />
                            ))}
                          </Box>
                        </Box>

                        {/* 荷台長さ */}
                        <Box sx={{ mb: 2, }}>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>荷台長さ</Typography>
                          <Box display="flex" flexDirection="row" gap={1}>
                            {DesignatedTruckLoadingPlatformLengthOptions.map((length) => (
                              <FormControlLabel
                                key={length}
                                control={
                                  <Checkbox
                                    checked={(watch("designatedTruckLoadingPlatformLengths") || []).includes(length)}
                                    onChange={(e) => {
                                      const currentLengths = watch("designatedTruckLoadingPlatformLengths") || [];
                                      const newLengths = e.target.checked
                                        ? [...currentLengths, length]
                                        : currentLengths.filter(l => l !== length);
                                      setValue("designatedTruckLoadingPlatformLengths", newLengths, { shouldDirty: true });
                                    }}
                                  />
                                }
                                label={length}
                              />
                            ))}
                          </Box>
                        </Box>

                        {/* 床仕様 */}
                        <Box sx={{ mb: 2, }}>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>床仕様</Typography>
                          <Box display="flex" flexDirection="row" gap={1}>
                            {DesignatedTruckFloorSpecificationOptions.map((floor) => (
                              <FormControlLabel
                                key={floor}
                                control={
                                  <Checkbox
                                    checked={(watch("designatedTruckFloorSpecifications") || []).includes(floor)}
                                    onChange={(e) => {
                                      const currentFloors = watch("designatedTruckFloorSpecifications") || [];
                                      const newFloors = e.target.checked
                                        ? [...currentFloors, floor]
                                        : currentFloors.filter(f => f !== floor);
                                      setValue("designatedTruckFloorSpecifications", newFloors, { shouldDirty: true });
                                    }}
                                  />
                                }
                                label={floor}
                              />
                            ))}
                          </Box>
                        </Box>

                        {/* 装置 */}
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>装置</Typography>
                          <Box display="flex" flexDirection="row" gap={1}>
                            {DesignatedTruckFeatureOptions.map((equipment) => (
                              <FormControlLabel
                                key={equipment}
                                control={
                                  <Checkbox
                                    checked={(watch("designatedTruckFeatures") || []).includes(equipment)}
                                    onChange={(e) => {
                                      const currentFeatures = watch("designatedTruckFeatures") || [];
                                      const newFeatures = e.target.checked
                                        ? [...currentFeatures, equipment]
                                        : currentFeatures.filter(eq => eq !== equipment);
                                      setValue("designatedTruckFeatures", newFeatures, { shouldDirty: true });
                                    }}
                                  />
                                }
                                label={equipment}
                              />
                            ))}
                          </Box>
                        </Box>
                      </Box>
                    </AccordionDetails>
                  </Accordion>

                  <Accordion sx={{ mt: 2 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography>指定トラック・NGドライバー（詳細設定）</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <TruckDriverConstraintsSelector
                        truckData={trucksOptions}
                        driverData={driversOptions}
                        allowedTrucks={allowedTrucks}
                        deniedDrivers={deniedDrivers}
                        onChangeAllowedTrucks={handleChangeAllowedTrucks}
                        onChangeDeniedDrivers={handleChangeDeniedDrivers}
                      />
                    </AccordionDetails>
                  </Accordion>

                  <Accordion sx={{ mt: 2 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography>請求情報（詳細設定）</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
                        {/* 請求合計金額（読み取り専用・最上部） */}
                        <Box sx={{ gridColumn: '1 / -1', mb: 0, p: 2, bgcolor: 'primary.50', borderRadius: 1 }}>
                          <Typography variant="h6" color="primary" gutterBottom>
                            請求合計金額: ¥{totalAmount.toLocaleString()}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ※ 下記項目の合計が自動計算されます
                          </Typography>
                        </Box>

                        <TextField
                          label="基本運賃"
                          type="number"
                          {...register("billing.basicFeeYen", { valueAsNumber: true })}
                          slotProps={{
                            input: {
                              startAdornment: <Typography>¥</Typography>,
                            },
                          }}
                        />
                        <TextField
                          label="サーチャージ料"
                          type="number"
                          {...register("billing.surchargeFeeYen", { valueAsNumber: true })}
                          slotProps={{
                            input: {
                              startAdornment: <Typography>¥</Typography>,
                            },
                          }}
                        />
                        <TextField
                          label="中継料"
                          type="number"
                          {...register("billing.relayFeeYen", { valueAsNumber: true })}
                          slotProps={{
                            input: {
                              startAdornment: <Typography>¥</Typography>,
                            },
                          }}
                        />
                        <TextField
                          label="高速代金"
                          type="number"
                          {...register("billing.highwayFeeYen", { valueAsNumber: true })}
                          slotProps={{
                            input: {
                              startAdornment: <Typography>¥</Typography>,
                            },
                          }}
                        />
                        <TextField
                          label="積込料金"
                          type="number"
                          {...register("billing.loadingFeeYen", { valueAsNumber: true })}
                          slotProps={{
                            input: {
                              startAdornment: <Typography>¥</Typography>,
                            },
                          }}
                        />
                        <TextField
                          label="付帯作業料金"
                          type="number"
                          {...register("billing.ancillaryFeeYen", { valueAsNumber: true })}
                          slotProps={{
                            input: {
                              startAdornment: <Typography>¥</Typography>,
                            },
                          }}
                        />
                        <TextField
                          label="待機料金"
                          type="number"
                          {...register("billing.waitingTimeFeeYen", { valueAsNumber: true })}
                          slotProps={{
                            input: {
                              startAdornment: <Typography>¥</Typography>,
                            },
                          }}
                        />
                        <TextField
                          label="取卸料"
                          type="number"
                          {...register("billing.unloadingFeeYen", { valueAsNumber: true })}
                          slotProps={{
                            input: {
                              startAdornment: <Typography>¥</Typography>,
                            },
                          }}
                        />
                        <TextField
                          label="諸経費"
                          type="number"
                          {...register("billing.expensesFeeYen", { valueAsNumber: true })}
                          slotProps={{
                            input: {
                              startAdornment: <Typography>¥</Typography>,
                            },
                          }}
                        />
                        <Box sx={{ gridColumn: '1 / -1' }}>
                          <TextField
                            label="付帯作業備考"
                            multiline
                            rows={2}
                            fullWidth
                            {...register("billing.ancillaryContent")}
                          />
                        </Box>
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </CardContent>
              </Card>
            </Box>

            {/* 固定ボタンエリア */}
            <Box
              sx={{
                position: 'fixed',
                bottom: 0,
                right: 0,
                left: 0,
                p: 2,
                bgcolor: 'white',
                borderTop: 1,
                borderColor: 'divider',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 2,
                zIndex: 1000
              }}
            >
              {!editable && (
                <Typography variant="subtitle1" color="error" sx={{ alignSelf: 'center' }}>
                  ※ 配車済みまたは分割されているため編集できません
                </Typography>
              )}
              <MuiButton
                variant="outlined"
                type="button"
                onClick={handleCancel}
                sx={{ minWidth: 100 }}
                disabled={isSubmitting}
              >
                キャンセル
              </MuiButton>
              <MuiButton
                variant="contained"
                type="submit"
                form="shipment-form"
                disabled={!editable || isSubmitting}
                startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : undefined}
                sx={{ minWidth: 100 }}
              >
                {isSubmitting ? '登録中...' : '登録'}
              </MuiButton>
            </Box>
          </Box>
        </LocalizationProvider>
      </DialogContent>
      <ConfirmDialog
        open={confirmDiaalogOpen}
        message="保存していない変更を破棄しますか？"
        handleOk={handleConfirmOK}
        handleCancel={handleConfirmCancel}
      />
    </Dialog>
  );
};
